import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TRowPreview } from './TRowPreview'
import { generateTRowUnits, createTRowConfig } from '@/lib/utils/t-hangar-generator'
import type { TRowConfigFormData, GeneratedUnit } from '@/types/hangar-units'
import type { TRowTypeConfig } from '@/types/hangars'

// Validation schema for T-row configuration
const tRowConfigSchema = z.object({
  unit_count: z.coerce
    .number()
    .min(1, 'At least 1 unit required')
    .max(50, 'Maximum 50 units'),
  nesting_style: z.enum(['standard', 'offset']),
  layout: z.enum(['single', 'nested']),
  door_width: z.coerce
    .number()
    .min(30, 'Door width must be at least 30 feet')
    .max(60, 'Door width must be 60 feet or less'),
  depth: z.coerce
    .number()
    .min(20, 'Depth must be at least 20 feet')
    .max(50, 'Depth must be 50 feet or less'),
  tail_width: z.coerce
    .number()
    .min(15, 'Tail width must be at least 15 feet')
    .max(40, 'Tail width must be 40 feet or less'),
  taxilane_width: z.coerce
    .number()
    .min(30, 'Taxilane must be at least 30 feet')
    .max(100, 'Taxilane must be 100 feet or less'),
})

interface TRowConfigWizardProps {
  /** Called when user clicks "Generate Units" with config and generated units */
  onGenerate: (config: TRowTypeConfig, units: GeneratedUnit[]) => void
  /** Called when user cancels */
  onCancel: () => void
  /** Initial config if editing */
  initialConfig?: TRowTypeConfig
  /** Whether generation is in progress */
  isLoading?: boolean
}

/**
 * Wizard for configuring T-hangar row parameters
 * Shows live preview of unit layout
 */
export function TRowConfigWizard({
  onGenerate,
  onCancel,
  initialConfig,
  isLoading,
}: TRowConfigWizardProps) {
  const form = useForm<TRowConfigFormData>({
    resolver: zodResolver(tRowConfigSchema) as any,
    defaultValues: {
      unit_count: initialConfig?.unit_count ?? 10,
      nesting_style: initialConfig?.nesting_style ?? 'standard',
      layout: initialConfig?.layout ?? 'single',
      door_width: initialConfig?.unit_template?.door_width ?? 42,
      depth: initialConfig?.unit_template?.depth ?? 32,
      tail_width: initialConfig?.unit_template?.tail_width ?? 24,
      taxilane_width: initialConfig?.shared_taxilane_width ?? 50,
    },
  })

  // Watch form values for live preview
  const formValues = form.watch()

  // Generate preview units based on current form values
  const previewUnits = useMemo(() => {
    if (
      formValues.unit_count > 0 &&
      formValues.door_width > 0 &&
      formValues.depth > 0 &&
      formValues.tail_width > 0
    ) {
      return generateTRowUnits(formValues)
    }
    return []
  }, [
    formValues.unit_count,
    formValues.door_width,
    formValues.depth,
    formValues.tail_width,
    formValues.nesting_style,
    formValues.layout,
    formValues.taxilane_width,
  ])

  const handleSubmit = (data: TRowConfigFormData) => {
    const config = createTRowConfig(data)
    const units = generateTRowUnits(data)
    onGenerate(config, units)
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">T-Hangar Configuration</h3>
        <p className="text-sm text-muted-foreground">
          Configure the unit template and layout for this T-hangar row
        </p>
      </div>

      <Form {...form}>
        <div className="space-y-6">
          {/* Number of units */}
          <FormField
            control={form.control as any}
            name="unit_count"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Number of Units</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  How many T-hangar units in this row
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Unit template dimensions */}
          <div className="space-y-4">
            <p className="text-sm font-medium">Unit Template (feet)</p>
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control as any}
                name="door_width"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Door Width</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="depth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Depth</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="tail_width"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tail Width</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Layout options */}
          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control as any}
              name="layout"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Layout</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="single">Single Row</SelectItem>
                      <SelectItem value="nested">Nested (Teeth)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Single or two-row layout
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control as any}
              name="taxilane_width"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Taxilane Width (ft)</FormLabel>
                  <FormControl>
                    <Input type="number" step="1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control as any}
              name="nesting_style"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nesting Style</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="offset">Offset (10% overlap)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Unit spacing
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Live Preview */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Preview</p>
            <div className="border rounded-md p-4 bg-white">
              <TRowPreview
                units={previewUnits}
                template={{
                  door_width: formValues.door_width || 42,
                  depth: formValues.depth || 32,
                  tail_width: formValues.tail_width || 24,
                }}
                taxilaneWidth={formValues.taxilane_width || 50}
                interactive={false}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isLoading}
              onClick={form.handleSubmit(handleSubmit) as any}
            >
              {isLoading ? 'Generating...' : 'Generate Units'}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  )
}
